// WHAT: Debug component for displaying TimeTracQ settings and workspace loading diagnostics
// WHY:  Provides detailed debug information to diagnose settings and workspace data loading issues
// DOMAIN: Settings → TimeTracQ → Debug
// DEPENDENCIES: Tauri core API, debug commands for store status and database content
// RISKS:
//   - WARN: Debug output may expose sensitive configuration and data
//   - NOTE: Raw JSON data is displayed which may be large and difficult to parse
//   - WARN: Error information may not provide actionable debugging steps

import { core } from "@tauri-apps/api";
import { Bug, Database, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";

import { Card, Button, Stack, Heading, Badge } from "@/components/ui";

/**
 * WHAT:
 *   Helper function to render debug badge based on data and error state.
 *
 * @param data - Data object (settings or workspaces)
 *
 * @param error - Error string if data loading failed
 *
 * @returns {React.JSX.Element} Badge component showing status
 *
 */
function renderDebugBadge(data: unknown, error: unknown): React.JSX.Element {
  if (data) {
    return (
      <Badge size="sm" style="subtle" variant="success">
        <CheckCircle className="w-3 h-3 mr-1" />
        Loaded
      </Badge>
    );
  }
  if (error) {
    return (
      <Badge size="sm" style="subtle" variant="destructive">
        <AlertCircle className="w-3 h-3 mr-1" />
        Error
      </Badge>
    );
  }
  return (
    <Badge size="sm" style="subtle" variant="default">
      Unknown
    </Badge>
  );
}

/**
 * WHAT:
 *   Helper function to render debug content (data or error).
 *
 * @param data - Data object to display
 *
 * @param error - Error string if data loading failed
 *
 * @returns {React.JSX.Element | null} Content component or null
 *
 */
function renderDebugContent(data: unknown, error: unknown): null | React.JSX.Element {
  if (data) {
    return (
      <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
        <pre className="text-sm text-green-300 overflow-x-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  }
  if (error) {
    const errorMessage = typeof error === "string" ? error : JSON.stringify(error);
    return (
      <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
        <div className="text-sm text-red-300 font-medium">Error:</div>
        <div className="text-sm text-red-400">{errorMessage}</div>
      </div>
    );
  }
  return null;
}

/**
 * WHAT:
 *   React component for displaying debug information about TimeTracQ settings and workspace loading.
 *
 * WHY:
 *   Helps diagnose issues with settings and workspace data loading by displaying
 *   store status, database content, and error information.
 *
 * BEHAVIOR:
 *   - Loads debug information automatically on mount
 *   - Displays store configuration status
 *   - Shows settings and workspaces data with error states
 *   - Provides raw JSON output for detailed inspection
 *   - Allows manual refresh of debug data
 *
 * RISKS:
 *   - WARN: Debug output contains sensitive configuration data
 *   - NOTE: Large JSON structures may impact rendering performance
 *   - WARN: Error messages may not provide specific root cause information
 *
 * @returns {JSX.Element} TimeTracQ debug component
 *
 */
// Reason: Component is used internally by TimeTracQSettingsView via routing, not directly imported

export function TimeTracQDebug() {
  const [debugData, setDebugData] = useState<unknown>(null);
  const [storeStatus, setStoreStatus] = useState<null | {
    status?: string;
    [key: string]: unknown;
  }>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /**
   * WHAT:
   *   Executes debug commands to gather diagnostic information about store and database state.
   *
   * WHY:
   *   Collects comprehensive debug data to help identify issues with settings and workspace loading.
   *
   * SIDE EFFECTS:
   *   - Invokes debug_store_status command to get store configuration
   *   - Invokes debug_database_content command to get settings and workspaces data
   *   - Updates component state with debug information
   *
   * RISKS:
   *   - WARN: Debug commands may expose sensitive configuration data
   *   - NOTE: Database content command may trigger connection pool initialization
   *   - WARN: Large data structures may impact performance
   */
  /**
   * WHAT:
   *   Runs debug tests to fetch store status and database content.
   *
   * WHY:
   *   To provide diagnostic information about the current state of TimeTracQ configuration and data.
   *
   * BEHAVIOR:
   *   - Invokes `debug_store_status` to get store configuration.
   *   - Invokes `debug_database_content` to get database content (settings, workspaces).
   *   - Stores results in `storeStatus` and `debugData` state.
   *   - Handles errors by setting error state.
   *
   * RISKS:
   *   - WARN: If Tauri commands fail, the component will show an error state.
   *   - NOTE: Database content may contain sensitive data; displayed in raw JSON format.
   */
  const runDebug = async () => {
    try {
      setLoading(true);
      setError(undefined);

      // NOTE: Debug Store Status - Gets configuration store state
      const store = await core.invoke<{
        status?: string;
        [key: string]: unknown;
      }>("debug_store_status");
      setStoreStatus(store);

      // NOTE: Debug Database Content - Gets settings and workspaces from database
      // WARN: This command may initialize database connection pool as side effect
      const databaseContent = await core.invoke<Record<string, unknown>>("debug_database_content");
      setDebugData(databaseContent);
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Debug failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void runDebug();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-orange-500/10 rounded-lg">
            <Bug className="w-6 h-6 text-orange-400" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              TimeTracQ Debug Information
            </Heading>
            <p className="text-slate-300">
              Debug information to diagnose TimeTracQ settings loading issues.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button
          disabled={loading}
          onClick={() => {
            void runDebug();
          }}
          size="md"
          variant="secondary"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Running Debug...
            </>
          ) : (
            <>
              <Bug className="w-4 h-4 mr-2" />
              Refresh Debug Info
            </>
          )}
        </Button>
      </div>

      {/* Store Status */}
      {storeStatus ? (
        <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
          <Stack direction="col" gap={4}>
            <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
              <Database className="w-5 h-5 text-slate-400" />
              Store Status
            </Heading>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(storeStatus).map(([key, value]) => (
                <div className="p-3 bg-slate-700/50 rounded-lg" key={key}>
                  <div className="text-sm font-medium text-slate-300 mb-1">{key}</div>
                  <div className="text-sm text-slate-400">
                    {typeof value === "string" ? value : JSON.stringify(value)}
                  </div>
                </div>
              ))}
            </div>
          </Stack>
        </Card>
      ) : null}

      {/* Database Content Debug */}
      {debugData ? (
        <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
          <Stack direction="col" gap={4}>
            <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
              <Database className="w-5 h-5 text-slate-400" />
              Database Content Debug
            </Heading>

            {/* Settings Debug */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Heading className="text-md font-medium text-white" level={4}>
                  Settings
                </Heading>
                {renderDebugBadge(
                  (debugData as { settings?: unknown }).settings,
                  (debugData as { settings_error?: unknown }).settings_error,
                )}
              </div>

              {renderDebugContent(
                (debugData as { settings?: unknown }).settings,
                (debugData as { settings_error?: unknown }).settings_error,
              )}
            </div>

            {/* Workspaces Debug */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Heading className="text-md font-medium text-white" level={4}>
                  Workspaces
                </Heading>
                {renderDebugBadge(
                  (debugData as { workspaces?: unknown }).workspaces,
                  (debugData as { workspaces_error?: unknown }).workspaces_error,
                )}
              </div>

              {renderDebugContent(
                (debugData as { workspaces?: unknown }).workspaces,
                (debugData as { workspaces_error?: unknown }).workspaces_error,
              )}
            </div>
          </Stack>
        </Card>
      ) : null}

      {/* Error Display */}
      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">Debug Error: {error}</span>
          </Stack>
        </Card>
      ) : null}

      {/* Raw Debug Data */}
      {debugData ? (
        <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
          <Stack direction="col" gap={4}>
            <Heading className="text-lg font-medium text-white" level={3}>
              Raw Debug Data
            </Heading>
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <pre className="text-sm text-slate-300 overflow-x-auto">
                {JSON.stringify(debugData, null, 2)}
              </pre>
            </div>
          </Stack>
        </Card>
      ) : null}
    </div>
  );
}
